/**
 * Componentes utilitários compartilhados pelas abas da tela de Configurações
 * do tenant (Conta, Credenciais, Assinatura Digital, Perfis, Indicação).
 *
 * Padrão visual: SaaS premium denso, monocromático, sem gradientes decorativos.
 *
 * RELEASE: 1.0.0 - 2026-05-03
 */

import * as React from 'react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/**
 * Linha de informação chave-valor usada dentro de cards/dl.
 * Label cinza à esquerda, valor à direita; densa e sem ícones decorativos.
 */
export function SettingsInfoRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 px-6 py-3">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );
}

/**
 * Card padrão de seção das abas de Configurações.
 * Borda sutil, sem sombra, sem gradiente.
 */
export function SettingsCard({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <Card className={cn('border-border/60 shadow-none', className)} {...props}>
      {children}
    </Card>
  );
}

/**
 * Cabeçalho padrão de uma seção (dentro de um SettingsCard).
 *
 * - eyebrow: rótulo pequeno em uppercase (ex.: "Workspace")
 * - title: título principal da seção
 * - description: subtítulo discreto opcional
 * - actions: slot direito (botões, badges)
 */
export function SettingsSectionHeader({
  eyebrow,
  title,
  description,
  actions,
  className,
}: {
  eyebrow?: string;
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-3 border-b border-border/50 px-6 py-4',
        className,
      )}
    >
      <div className="min-w-0">
        {eyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {eyebrow}
          </p>
        )}
        <h3 className="mt-0.5 text-lg font-semibold tracking-tight text-foreground">
          {title}
        </h3>
        {description && (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

/**
 * Banner informativo neutro (sem cores agressivas).
 * Usado para avisos discretos dentro das abas.
 */
export function SettingsInfoBanner({
  icon: Icon,
  title,
  children,
  className,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  title?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border border-border/60 bg-muted/30 p-4',
        className,
      )}
    >
      {Icon && (
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      )}
      <div className="space-y-1 text-sm">
        {title && <p className="font-medium text-foreground">{title}</p>}
        <div className="text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}

/**
 * Bullet de status discreto (●). Usado em badges/linhas para indicar estado
 * sem aplicar fundos coloridos saturados.
 */
export function StatusDot({
  tone = 'muted',
  className,
}: {
  tone?: 'emerald' | 'amber' | 'destructive' | 'primary' | 'muted';
  className?: string;
}) {
  const map: Record<string, string> = {
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    destructive: 'bg-destructive',
    primary: 'bg-primary',
    muted: 'bg-muted-foreground/40',
  };
  return (
    <span
      className={cn('inline-block h-1.5 w-1.5 rounded-full', map[tone], className)}
    />
  );
}
